import { type RefObject, useEffect, useState } from 'react'

type UseFocusWithinOptions = {
  /**
   * Additional portaled roots that logically belong to
   * the same focus region, like contextmenu, dialog etc.
   */
  additionalRoots?: readonly RefObject<HTMLElement | null>[]
}

/**
 * Reusable hook that detects whether any elements inside it are focused.
 */
export function useFocusWithin(
  rootRef: RefObject<HTMLElement | null>,
  { additionalRoots = [] }: UseFocusWithinOptions = {},
): boolean {
  const [hasFocus, setHasFocus] = useState(false)

  useEffect(() => {
    let disposed = false
    let checkVersion = 0

    function contains(element: Element | null): boolean {
      if (!element) return false

      if (rootRef.current?.contains(element)) {
        return true
      }

      return additionalRoots.some(
        (ref) => ref.current?.contains(element) ?? false,
      )
    }

    function update() {
      if (disposed) return

      const next = document.hasFocus() && contains(document.activeElement)

      setHasFocus((previous) => (previous === next ? previous : next))
    }

    function updateAfterFocusSettles() {
      const version = ++checkVersion

      queueMicrotask(() => {
        if (version === checkVersion) {
          update()
        }
      })
    }

    function handleWindowBlur() {
      ++checkVersion
      setHasFocus(false)
    }

    update()

    document.addEventListener('focusin', update, true)
    document.addEventListener('focusout', updateAfterFocusSettles, true)
    window.addEventListener('focus', updateAfterFocusSettles)
    window.addEventListener('blur', handleWindowBlur)

    return () => {
      disposed = true
      ++checkVersion

      document.removeEventListener('focusin', update, true)
      document.removeEventListener('focusout', updateAfterFocusSettles, true)
      window.removeEventListener('focus', updateAfterFocusSettles)
      window.removeEventListener('blur', handleWindowBlur)
    }
  }, [rootRef, additionalRoots])

  return hasFocus
}
